import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { app } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './OrderInfo.css';

const db = getFirestore(app);

type OrderItem = {
    productId: string;
    title: string;
    quantity: number;
    qty: number;
    price: number;
    variantId?: string;
    remark?: string;
    renewTerm?: string;
    restockedQty?: number;
    imageUrl?: string;
};

type Order = {
    id: string;
    orderId: string;
    userId: string;
    products: OrderItem[];
    total: number;
    subtotal: number;
    tax: number;
    shippingFee: number;
    discount: number;
    amountPaid: number;
    status: string;
    createdAt: any;
    pay: boolean;
    city: string;
    province: string;
    houseStreet: string;
    suite: string;
    postalCode: string;
    deliverMethod: string;
    shipType: string;
    pickupDate: string;
    pickupstatus: boolean;
    expireDate: string;
    rentalTerm: string;
    renewed: boolean;
    return: boolean;
    viewed: boolean;
    deleted: boolean;
    insurance: boolean;
    insuranceFee: number;
    oneWayOption: string;
    promotion: number;
    taxEnabled: boolean;
    shippingDistance: number;
    renewSubtotal?: number;
    renewTax?: number;
    renewTerm?: string;
    renewTotal?: number;
    taxEnabledForRenew?: boolean;
    renewHistory?: any[];
    pdfs?: any[];
};

const OrderInfo: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [showOrderDetail, setShowOrderDetail] = useState(false);
    const [productImages, setProductImages] = useState<{ [key: string]: string }>({});

    // 获取商品图片的函数
    const getProductImage = async (productId: string, variantId?: string) => {
        try {
            // 1. 先从 localStorage 读取产品数据
            const cached = localStorage.getItem(`product_${productId}`);
            const cacheTime = localStorage.getItem(`product_${productId}_time`);
            const now = Date.now();
            const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
            const maxAge = 5 * 60 * 1000; // 5分钟缓存

            let productData: any = null;

            if (cached && cacheAge < maxAge) {
                try {
                    productData = JSON.parse(cached);
                } catch { }
            }

            // 2. 如果缓存不存在或过期，从Firestore获取
            if (!productData) {
                const productDoc = await getDoc(doc(db, 'products', productId));
                if (productDoc.exists()) {
                    productData = productDoc.data();

                    // 3. 缓存产品数据
                    localStorage.setItem(`product_${productId}`, JSON.stringify(productData));
                    localStorage.setItem(`product_${productId}_time`, Date.now().toString());
                }
            }

            if (productData) {
                // 参考CategoryListing的处理方式
                const variantImages = productData.variants?.map((v: any) => v.image).filter(Boolean) || [];
                const allImages = [...variantImages, ...(productData.images || [])];

                // 如果有variantId，尝试找到对应的variant图片
                if (variantId && productData.variants) {
                    const variant = productData.variants.find((v: any) => v.id === variantId);
                    if (variant && variant.image) {
                        return variant.image;
                    }
                }

                // 返回第一张可用的图片
                return allImages[0] || '';
            }
        } catch (error) {
            console.error('Error fetching product image:', error);
        }
        return '';
    };

    const fetchProductImages = useCallback(async (orders: Order[]) => {
        const images: { [key: string]: string } = {};
        for (const order of orders) {
            for (const product of order.products) {
                // 为每个商品创建唯一的key，包含variantId
                const imageKey = product.variantId ? `${product.productId}_${product.variantId}` : product.productId;

                if (!images[imageKey]) {
                    const imageUrl = await getProductImage(product.productId, product.variantId);
                    if (imageUrl) {
                        images[imageKey] = imageUrl;
                    }
                }
            }
        }
        setProductImages(images);
    }, []);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        const fetchOrders = async () => {
            try {
                // 1. 先从 localStorage 读取订单数据
                const cached = localStorage.getItem(`orders_${user.uid}`);
                const cacheTime = localStorage.getItem(`orders_${user.uid}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 5 * 60 * 1000; // 5分钟缓存

                if (cached && cacheAge < maxAge) {
                    try {
                        const ordersData = JSON.parse(cached);
                        setOrders(ordersData);
                        setLoading(false);

                        // 获取商品图片
                        fetchProductImages(ordersData);
                        return;
                    } catch { }
                }

                console.log('Fetching orders for user:', user.uid);

                // 2. 从Firestore获取订单数据
                // 尝试不同的用户ID字段名，但不使用orderBy来避免索引问题
                let ordersQuery = query(
                    collection(db, 'orders'),
                    where('userId', '==', user.uid)
                );

                let querySnapshot = await getDocs(ordersQuery);

                // 如果没有找到订单，尝试其他可能的字段名
                if (querySnapshot.docs.length === 0) {
                    console.log('No orders found with userId, trying user_id...');
                    ordersQuery = query(
                        collection(db, 'orders'),
                        where('user_id', '==', user.uid)
                    );
                    querySnapshot = await getDocs(ordersQuery);
                }

                // 如果还是没有，尝试uid字段
                if (querySnapshot.docs.length === 0) {
                    console.log('No orders found with user_id, trying uid...');
                    ordersQuery = query(
                        collection(db, 'orders'),
                        where('uid', '==', user.uid)
                    );
                    querySnapshot = await getDocs(ordersQuery);
                }

                console.log('Found orders:', querySnapshot.docs.length);
                const ordersData: Order[] = [];

                for (const orderDoc of querySnapshot.docs) {
                    const orderData = orderDoc.data() as Order;
                    console.log('Order data:', orderData);

                    // 验证和清理订单数据
                    const cleanOrderData = {
                        ...orderData,
                        id: orderDoc.id,
                        orderId: orderData.orderId || '',
                        total: orderData.total || 0,
                        subtotal: orderData.subtotal || 0,
                        tax: orderData.tax || 0,
                        shippingFee: orderData.shippingFee || 0,
                        discount: orderData.discount || 0,
                        amountPaid: orderData.amountPaid || 0,
                        status: orderData.status || 'Pending',
                        pay: orderData.pay || false,
                        city: orderData.city || '',
                        province: orderData.province || '',
                        houseStreet: orderData.houseStreet || '',
                        suite: orderData.suite || '',
                        postalCode: orderData.postalCode || '',
                        deliverMethod: orderData.deliverMethod || '',
                        shipType: orderData.shipType || '',
                        pickupDate: orderData.pickupDate || '',
                        pickupstatus: orderData.pickupstatus || false,
                        expireDate: orderData.expireDate || '',
                        rentalTerm: orderData.rentalTerm || '',
                        renewed: orderData.renewed || false,
                        return: orderData.return || false,
                        viewed: orderData.viewed || false,
                        deleted: orderData.deleted || false,
                        insurance: orderData.insurance || false,
                        insuranceFee: orderData.insuranceFee || 0,
                        oneWayOption: orderData.oneWayOption || '',
                        promotion: orderData.promotion || 0,
                        taxEnabled: orderData.taxEnabled || false,
                        shippingDistance: orderData.shippingDistance || 0,
                        createdAt: orderData.createdAt || new Date()
                    };

                    // 清理商品数据
                    cleanOrderData.products = (orderData.products || []).map((item: any) => ({
                        productId: item.productId || '',
                        title: item.title || 'Unknown Product',
                        quantity: item.quantity || 0,
                        qty: item.qty || 0,
                        price: item.price || 0,
                        variantId: item.variantId || '',
                        remark: item.remark || '',
                        renewTerm: item.renewTerm || '',
                        restockedQty: item.restockedQty || 0
                    }));

                    ordersData.push(cleanOrderData);
                }

                // 在客户端按创建时间排序
                ordersData.sort((a, b) => {
                    const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                    const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                    return dateB.getTime() - dateA.getTime(); // 降序排列
                });

                setOrders(ordersData);

                // 缓存订单数据
                localStorage.setItem(`orders_${user.uid}`, JSON.stringify(ordersData));
                localStorage.setItem(`orders_${user.uid}_time`, Date.now().toString());

                await fetchProductImages(ordersData);
            } catch (error) {
                console.error('Error fetching orders:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [user, navigate, fetchProductImages]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return '#ff9800';
            case 'processing': return '#2196f3';
            case 'shipped': return '#9c27b0';
            case 'delivered': return '#4caf50';
            case 'cancelled': return '#f44336';
            default: return '#666';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending': return 'Pending';
            case 'processing': return 'Processing';
            case 'shipped': return 'Shipped';
            case 'delivered': return 'Delivered';
            case 'cancelled': return 'Cancelled';
            default: return status;
        }
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleOrderClick = (order: Order) => {
        setSelectedOrder(order);
        setShowOrderDetail(true);
    };

    const closeOrderDetail = () => {
        setShowOrderDetail(false);
        setSelectedOrder(null);
    };





    if (loading) {
        return (
            <div className="order-info-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading orders...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="order-info-container">
            <div className="order-info-header">
                <button
                    className="back-button"
                    onClick={() => navigate('/profile')}
                >
                    ← Back
                </button>
                <h1>My Orders</h1>
            </div>

            {orders.length === 0 ? (
                <div className="no-orders">
                    <div className="no-orders-icon">📦</div>
                    <h2>No Orders Yet</h2>
                    <p>You haven't placed any orders yet.</p>
                    <button
                        className="shop-now-button"
                        onClick={() => navigate('/')}
                    >
                        Start Shopping
                    </button>
                </div>
            ) : (
                <div className="orders-grid">
                    {orders.map((order) => (
                        <div
                            key={order.id}
                            className="order-card"
                            onClick={() => handleOrderClick(order)}
                        >
                            <div className="order-header">
                                <div className="order-id">Order #{order.orderId}</div>
                                <div
                                    className="order-status"
                                    style={{ backgroundColor: getStatusColor(order.status) }}
                                >
                                    {getStatusText(order.status)}
                                </div>
                            </div>
                            <div className="order-details">
                                <div className="order-date">
                                    {formatDate(order.createdAt)}
                                </div>
                                <div className="order-total">
                                    ${(order.total || 0).toFixed(2)}
                                </div>
                                <div className="order-items">
                                    {order.products.length} item{order.products.length !== 1 ? 's' : ''}
                                </div>
                                <div className="order-payment">
                                    {order.pay ? 'Paid' : 'Unpaid'}
                                </div>
                            </div>
                            <div className="order-preview">
                                {order.products.slice(0, 3).map((item: any, index: number) => (
                                    <div key={index} className="order-item-preview">
                                        {(() => {
                                            const imageKey = item.variantId ? `${item.productId}_${item.variantId}` : item.productId;
                                            return productImages[imageKey] ? (
                                                <img
                                                    src={productImages[imageKey]}
                                                    alt={item.title}
                                                    className="preview-item-image"
                                                    onError={(e) => {
                                                        const target = e.currentTarget as HTMLElement;
                                                        target.style.display = 'none';
                                                        const placeholder = target.nextElementSibling as HTMLElement;
                                                        if (placeholder) {
                                                            placeholder.style.display = 'flex';
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <div className="preview-image-placeholder">
                                                    {item.title.charAt(0).toUpperCase()}
                                                </div>
                                            );
                                        })()}
                                        <span className="item-name">{item.title}</span>
                                        <span className="item-quantity">x{item.quantity}</span>
                                        <span className="item-price">${(item.price || 0).toFixed(2)}</span>
                                    </div>
                                ))}
                                {order.products.length > 3 && (
                                    <div className="more-items">
                                        +{order.products.length - 3} more
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Order Detail Modal */}
            {showOrderDetail && selectedOrder && (
                <div className="order-detail-modal" onClick={closeOrderDetail}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Order #{selectedOrder.orderId}</h2>
                            <button className="close-button" onClick={closeOrderDetail}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="order-info-section">
                                <div className="info-row">
                                    <span className="label">Status:</span>
                                    <span
                                        className="status-badge"
                                        style={{ backgroundColor: getStatusColor(selectedOrder.status) }}
                                    >
                                        {getStatusText(selectedOrder.status)}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Order Date:</span>
                                    <span>{formatDate(selectedOrder.createdAt)}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Order Number:</span>
                                    <span>{selectedOrder.orderId}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Total Amount:</span>
                                    <span className="total-amount">${(selectedOrder.total || 0).toFixed(2)}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Amount Paid:</span>
                                    <span className="amount-paid">${(selectedOrder.amountPaid || 0).toFixed(2)}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Payment Status:</span>
                                    <span className={selectedOrder.pay ? 'paid-status' : 'unpaid-status'}>
                                        {selectedOrder.pay ? 'Paid' : 'Unpaid'}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Shipping Address:</span>
                                    <span>{selectedOrder.suite} {selectedOrder.houseStreet}, {selectedOrder.city}, {selectedOrder.province} {selectedOrder.postalCode}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Delivery Method:</span>
                                    <span>{selectedOrder.deliverMethod}</span>
                                </div>
                            </div>

                            <div className="order-items-section">
                                <h3>Order Items</h3>
                                <div className="items-list">
                                    {selectedOrder.products.map((item: any, index: number) => (
                                        <div key={index} className="order-item">
                                            <div className="item-image-container">
                                                {(() => {
                                                    const imageKey = item.variantId ? `${item.productId}_${item.variantId}` : item.productId;
                                                    return productImages[imageKey] ? (
                                                        <img
                                                            src={productImages[imageKey]}
                                                            alt={item.title}
                                                            className="item-image"
                                                            onError={(e) => {
                                                                const target = e.currentTarget as HTMLElement;
                                                                target.style.display = 'none';
                                                                const placeholder = target.nextElementSibling as HTMLElement;
                                                                if (placeholder) {
                                                                    placeholder.style.display = 'flex';
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="item-image-placeholder">
                                                            {item.title.charAt(0).toUpperCase()}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            <div className="item-details">
                                                <div className="item-name">{item.title}</div>
                                                <div className="item-price">${(item.price || 0).toFixed(2)}</div>
                                                {item.variantId && (
                                                    <div className="item-variant">Variant ID: {item.variantId}</div>
                                                )}
                                            </div>
                                            <div className="item-quantity">
                                                x{item.quantity}
                                            </div>
                                            <div className="item-total">
                                                ${((item.price || 0) * (item.quantity || 0)).toFixed(2)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderInfo; 